import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Package, ArrowLeft, Edit, Tag, Boxes, Ruler, Barcode, Building2, Layers } from 'lucide-react';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import { supplierService } from '../services/supplierService';
import { packagingService } from '../services/packagingService';
import { brandService } from '../services/brandService';
import { ProductResponseDTO } from '../types/product';
import alertService from '../services/alertService';

const currency = (v?: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatDateTime = (iso?: string) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
  } catch {
    return iso || '-';
  }
};

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<ProductResponseDTO | null>(null);
  const [categoryName, setCategoryName] = useState<string>('-');
  const [brandName, setBrandName] = useState<string>('-');
  const [packagingLabel, setPackagingLabel] = useState<string>('-');
  const [supplierName, setSupplierName] = useState<string>('-');

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const resp = await productService.getById(id);
      const raw: any = resp?.data;
      const data: any = raw && raw.data ? raw.data : raw;
      setProduct(data || null);
      if (data) {
        setBrandName(data.marca || '-');
        setPackagingLabel(data.embalagemSigla || data.embalagemId || '-');
        if (data.categoriaId) {
          try {
            const c = await categoryService.getById(String(data.categoriaId));
            setCategoryName(c?.data?.nome || String(data.categoriaId));
          } catch {
            setCategoryName(String(data.categoriaId));
          }
        } else {
          setCategoryName('-');
        }
        if (data.marcaId && !data.marca) {
          try {
            const b = await brandService.getById(String(data.marcaId));
            setBrandName(b?.data?.nome || String(data.marcaId));
          } catch {
            setBrandName(String(data.marcaId));
          }
        }
        if (data.embalagemId) {
          try {
            const p = await packagingService.getById(String(data.embalagemId));
            const desc = p?.data?.descricao || '';
            const sigla = p?.data?.sigla || '';
            setPackagingLabel(sigla ? `${desc ? `${desc} (${sigla})` : sigla}` : (desc || String(data.embalagemId)));
          } catch {
            setPackagingLabel(data.embalagemSigla || String(data.embalagemId));
          }
        }
        if (data.fornecedorId) {
          try {
            const s = await supplierService.getById(String(data.fornecedorId));
            const nome = s?.data?.nomeFantasia || s?.data?.razaoSocial || '';
            setSupplierName(nome || String(data.fornecedorId));
          } catch {
            setSupplierName(String(data.fornecedorId));
          }
        } else {
          setSupplierName('-');
        }
      } else {
        setCategoryName('-');
        setBrandName('-');
        setPackagingLabel('-');
        setSupplierName('-');
      }
    } catch (err: any) {
      alertService.error('Erro ao carregar produto', err?.message || 'Tente novamente');
      navigate('/produtos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64 text-gray-600">
          <Package className="h-6 w-6 animate-pulse" />
          <span className="ml-3">Carregando produto...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Produto não encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">O produto solicitado não foi encontrado.</p>
          <div className="mt-6">
            <button onClick={() => navigate('/produtos')} className="berry-button-primary w-full sm:w-auto">
              Voltar para Produtos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/produtos')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Package className="h-6 w-6 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Detalhe do Produto</h1>
            <p className="text-sm sm:text-base text-gray-600">Informações e dados do produto</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3 sm:mt-0">
          <button onClick={() => navigate(`/produtos/${product.id}`)} className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center gap-2 w-full sm:w-auto">
            <Edit className="h-4 w-4" /> Editar Produto
          </button>
          <button onClick={() => navigate(`/estoque/movimentacoes/produto/${product.id}`)} className="berry-button-secondary inline-flex items-center gap-2 w-full sm:w-auto">
            <Boxes className="h-4 w-4" /> Estoque
          </button>
          <button onClick={() => navigate(`/precos/produto/${product.id}/historico`)} className="berry-button-secondary inline-flex items-center gap-2 w-full sm:w-auto">
            <Tag className="h-4 w-4" /> Preço
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="h-5 w-5 text-gray-600" />
            <h2 className="text-sm sm:text-base font-semibold text-gray-800">Informações gerais</h2>
          </div>
          <div className="space-y-2">
            <div>
              <div className="text-xs text-gray-500">Nome/Descrição</div>
              <div className="text-sm text-gray-900 font-medium">{product.nome}</div>
            </div>
            {!product.descricao &&(
              <div>
                <div className="text-xs text-gray-500">Descrição</div>
                <div className="text-sm text-gray-700  font-medium">{product.descricao}</div>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-500">Status</div>
              <div className="text-sm">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${product.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                  {product.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Barcode className="h-5 w-5 text-gray-600" />
            <h2 className="text-sm sm:text-base font-semibold text-gray-800">Identificadores</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-500">Código</div>
              <div className="text-sm text-gray-900 font-medium">{product.codigo || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Código de Barras</div>
              <div className="text-sm text-gray-900">{product.codigoBarras || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Categoria</div>
              <div className="text-sm text-gray-900">{categoryName || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Marca</div>
              <div className="text-sm text-gray-900">{brandName || '-'}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Ruler className="h-5 w-5 text-gray-600" />
            <h2 className="text-sm sm:text-base font-semibold text-gray-800">Dimensões</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <div className="text-xs text-gray-500">Peso</div>
              <div className="text-sm text-gray-900">{product.peso ?? '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Altura</div>
              <div className="text-sm text-gray-900">{product.altura ?? '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Largura</div>
              <div className="text-sm text-gray-900">{product.largura ?? '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Profundidade</div>
              <div className="text-sm text-gray-900">{product.profundidade ?? '-'}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Boxes className="h-5 w-5 text-gray-600" />
            <h2 className="text-sm sm:text-base font-semibold text-gray-800">Estoque e embalagem</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-500">Controla estoque</div>
              <div className="text-sm text-gray-900">{product.controlaEstoque ? 'Sim' : 'Não'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Permite venda com estoque negativo</div>
              <div className="text-sm text-gray-900">{product.permiteVendaEstoqueNegativo ? 'Sim' : 'Não'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Estoque mínimo</div>
              <div className="text-sm text-gray-900">{product.estoqueMinimo ?? '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Estoque máximo</div>
              <div className="text-sm text-gray-900">{product.estoqueMaximo ?? '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Embalagem</div>
              <div className="text-sm text-gray-900">{packagingLabel || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Fornecedor</div>
              <div className="text-sm text-gray-900">{supplierName || '-'}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-5 w-5 text-gray-600" />
            <h2 className="text-sm sm:text-base font-semibold text-gray-800">Fiscal/identificação</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="text-xs text-gray-500">NCM</div>
              <div className="text-sm text-gray-900">{product.ncm || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">CEST</div>
              <div className="text-sm text-gray-900">{product.cest || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Tenant</div>
              <div className="text-sm text-gray-900">{product.tenantId || '-'}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <div className="text-xs text-gray-500">Criado em</div>
              <div className="text-sm text-gray-900">{formatDateTime(product.createdAt) || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Atualizado em</div>
              <div className="text-sm text-gray-900">{formatDateTime(product.updatedAt) || '-'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
